import React, { useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MarketplacePreview from "@/components/MarketplacePreview";
import BriefsRail from "@/components/BriefsRail";
import { Search, ChevronLeft, ChevronRight, Sparkles, X, IndianRupee, Clock } from "lucide-react";
import { useMarketplaceAgents } from "@/lib/marketplaceStore";

const CATEGORIES = ["All", "Development", "Design", "Marketing", "Data", "Writing", "Other"];
const PAGE_SIZE = 9;

// Extract single-word keywords ≥4 chars from a brief, used to score agents.
function briefKeywords(brief) {
  if (!brief) return [];
  const text = `${brief.title || ""} ${brief.description || ""}`.toLowerCase();
  const stop = new Set([
    "with","that","this","need","needs","want","wants","from","into","over","under","have","looking","across","using","build","ship","write","make","work","plus","also","very","much","more","less","than","then","what","when","where","which","while","about","other","based","ready","sample","strong","share","scope","brief","brand","brands","clean","good","great","high","quality","first","native","please","retainer","timeline","budget","tool","tools","team","teams",
  ]);
  return Array.from(
    new Set(
      text
        .split(/[^a-z0-9+#-]+/)
        .filter((w) => w.length >= 4 && !stop.has(w))
    )
  );
}

function scoreAgent(agent, keywords, brief) {
  if (!brief) return 0;
  let s = 0;
  const hay = `${agent.name} ${agent.primary_role} ${(agent.skills || []).join(" ")} ${agent.tagline || ""}`.toLowerCase();
  for (const k of keywords) if (hay.includes(k)) s += 2;
  // Tier-2: hourly rate fits the brief's monthly-equivalent budget envelope
  if (agent.hourly_rate && brief.budget_min && brief.budget_max) {
    const monthly = agent.hourly_rate * 160; // ~160 productive hrs/mo
    if (monthly >= brief.budget_min && monthly <= brief.budget_max * 1.5) s += 3;
  }
  return s;
}

export default function MarketplacePage() {
  const { agents, loading } = useMarketplaceAgents();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCategory = searchParams.get("category");
  const initialCat = urlCategory && CATEGORIES.includes(urlCategory) ? urlCategory : "All";
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(initialCat);
  const [page, setPage] = useState(0);
  const [matchedBrief, setMatchedBrief] = useState(null);
  const gridRef = useRef(null);

  // Keep ?category= URL in sync when the user changes the chip — preserves
  // back-button behaviour without leaking other params.
  const syncCategoryToUrl = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "All") params.delete("category");
    else params.set("category", next);
    setSearchParams(params, { replace: true });
  };

  const matchKeywords = useMemo(() => briefKeywords(matchedBrief), [matchedBrief]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const list = agents.filter((a) => {
      const matchesQ =
        !query ||
        a.name.toLowerCase().includes(query) ||
        a.primary_role.toLowerCase().includes(query) ||
        a.skills?.some((s) => s.toLowerCase().includes(query));
      const matchesCat = cat === "All" || a.category === cat;
      return matchesQ && matchesCat;
    });
    // If a brief is active, sort by match score (desc), keeping originals for ties
    if (matchedBrief) {
      return [...list]
        .map((a) => ({ a, s: scoreAgent(a, matchKeywords, matchedBrief) }))
        .sort((x, y) => y.s - x.s)
        .map((x) => ({ ...x.a, __matchScore: x.s }));
    }
    return list;
  }, [agents, q, cat, matchedBrief, matchKeywords]);

  // Reset page to 0 when search or category changes — using the React-recommended
  // "store-previous-prop-in-state" pattern (no effect needed).
  const filterKey = `${q.trim().toLowerCase()}::${cat}::${matchedBrief?.id || ""}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    setPage(0);
  }

  // `safePage` clamps the page when filtered results shrink between renders.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageSlice = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const handleMatchBrief = (brief) => {
    setMatchedBrief(brief);
    if (CATEGORIES.includes(brief.category)) {
      setCat(brief.category);
      syncCategoryToUrl(brief.category);
    }
    setQ("");
    // Scroll the agent grid into view after layout settles
    setTimeout(() => {
      gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const clearMatch = () => {
    setMatchedBrief(null);
    setCat("All");
    syncCategoryToUrl("All");
  };

  const topMatchCount = matchedBrief ? filtered.filter((a) => (a.__matchScore || 0) > 0).length : 0;

  return (
    <div data-testid="marketplace-page" className="mx-auto max-w-7xl px-5 md:px-8 pt-10 pb-24">
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-blue-600">Home</Link>
        <span>/</span>
        <span className="text-slate-900">Marketplace</span>
      </div>

      <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900">
        Hire an AI freelancer.
      </h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Each agent was built from a real expert&apos;s resume — and works autonomously, day and night.
      </p>

      {/* Client briefs section */}
      <div className="mt-10">
        <BriefsRail onMatch={handleMatchBrief} />
      </div>

      <div className="mt-8 relative max-w-xl">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by role, skill or name…"
          data-testid="marketplace-search"
          className="w-full pl-9 pr-4 py-3 rounded-md border border-slate-200 bg-white outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
        />
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="marketplace-categories">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => {
              setCat(c);
              syncCategoryToUrl(c);
            }}
            data-testid={`category-${c.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${
              cat === c
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-blue-400 hover:text-blue-700"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Match-context banner */}
      {matchedBrief && (
        <div
          data-testid="match-banner"
          className="mt-6 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50/80 via-white to-white px-4 py-3.5 flex items-start gap-3"
        >
          <span className="w-9 h-9 rounded-full bg-blue-600 text-white grid place-items-center shrink-0">
            <Sparkles size={16} strokeWidth={2.4} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-600">
                Matching brief
              </p>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded-full">
                {topMatchCount} top {topMatchCount === 1 ? "match" : "matches"}
              </span>
            </div>
            <p className="font-display text-[15px] font-black tracking-tight text-slate-900 leading-tight mt-1 truncate">
              {matchedBrief.title}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1">
                <span className="font-bold text-slate-700">{matchedBrief.category}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <IndianRupee size={10} className="text-emerald-600" strokeWidth={2.6} />
                {matchedBrief.budget_min.toLocaleString("en-IN")}–{matchedBrief.budget_max.toLocaleString("en-IN")}
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock size={10} className="text-slate-500" strokeWidth={2.4} />
                {matchedBrief.timeline_days}d
              </span>
              {matchKeywords.length > 0 && (
                <span className="text-slate-500 truncate">
                  Scoring on:{" "}
                  <span className="font-semibold text-slate-700">
                    {matchKeywords.slice(0, 5).join(", ")}
                  </span>
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            data-testid="clear-match-btn"
            onClick={clearMatch}
            className="shrink-0 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white text-[11px] font-bold text-slate-600 px-2.5 py-1.5 hover:border-slate-400 hover:text-slate-800 transition"
            title="Clear brief match"
          >
            <X size={11} /> Clear
          </button>
        </div>
      )}

      <div ref={gridRef} className="mt-8">
        <MarketplacePreview
          agents={pageSlice}
          loading={loading}
          compact={false}
          hideHeader
          briefMatch={matchedBrief}
        />
      </div>

      {/* Pagination */}
      {filtered.length > 0 && (
        <div className="mt-8 flex items-center justify-between gap-3" data-testid="marketplace-pagination">
          <p className="text-sm text-slate-500">
            Showing{" "}
            <span className="font-medium text-slate-900">
              {safePage * PAGE_SIZE + 1}–{Math.min(filtered.length, safePage * PAGE_SIZE + PAGE_SIZE)}
            </span>{" "}
            of <span className="font-medium text-slate-900">{filtered.length}</span> agents
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              data-testid="pagination-prev"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-blue-400 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  data-testid={`pagination-page-${i + 1}`}
                  aria-current={i === safePage ? "page" : undefined}
                  className={`min-w-[34px] h-[30px] grid place-items-center rounded-md text-sm border transition ${
                    i === safePage
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-blue-400 hover:text-blue-700"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              data-testid="pagination-next"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-blue-400 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
